import { useMemo } from "react";
import { fmt } from "../lib/constants";

const W = 180;
const H = 40;
const PAD = 3;

const dayLabel = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

// 30-day sparkline of open CRM issues (first snapshot of each day + today's live value)
export const HygieneTrend = ({ trend, className = "" }) => {
  const geo = useMemo(() => {
    const pts = trend || [];
    if (pts.length < 2) return null;
    const vals = pts.map((p) => p.total);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const span = max - min || 1;
    const x = (i) => PAD + (i / (pts.length - 1)) * (W - PAD * 2);
    const y = (v) => H - PAD - ((v - min) / span) * (H - PAD * 2);
    const line = pts.map((p, i) => `${x(i).toFixed(1)},${y(p.total).toFixed(1)}`).join(" ");
    const area = `${x(0).toFixed(1)},${H} ${line} ${x(pts.length - 1).toFixed(1)},${H}`;
    const last = pts[pts.length - 1];
    const first = pts[0];
    const change = last.total - first.total;
    return { line, area, min, max, last, first, change, lx: x(pts.length - 1), ly: y(last.total) };
  }, [trend]);

  if (!geo) {
    return (
      <div data-testid="hygiene-trend-empty" className={`text-[11px] text-slate-400 ${className}`}>
        Trend builds up from tomorrow – one point per day.
      </div>
    );
  }
  const up = geo.change > 0;
  return (
    <div data-testid="hygiene-trend" className={`flex items-center gap-3 ${className}`} title={`Open issues per day since ${dayLabel(geo.first.day)} (first check of each day; today is live)`}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="shrink-0 overflow-visible" aria-hidden="true">
        <polygon points={geo.area} fill="rgb(220 42 39 / 0.12)" />
        <polyline points={geo.line} fill="none" stroke="#DC2A27" strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={geo.lx} cy={geo.ly} r={3} fill="#DC2A27" data-testid="hygiene-trend-today" />
      </svg>
      <div className="text-[11px] leading-tight text-slate-500 min-w-0">
        <div className="num whitespace-nowrap">
          <span className="text-slate-400">{dayLabel(geo.first.day)}</span> <b className="text-ink">{fmt(geo.first.total)}</b> → <span className="text-slate-400">today</span> <b className="text-ink">{fmt(geo.last.total)}</b>
        </div>
        <div className={`num font-semibold ${up ? "text-brand" : geo.change < 0 ? "text-live" : "text-slate-400"}`} data-testid="hygiene-trend-change">
          {up ? "+" : geo.change < 0 ? "−" : "±"}{fmt(Math.abs(geo.change))} over {trend.length - 1} {trend.length - 1 === 1 ? "day" : "days"} · range {fmt(geo.min)}–{fmt(geo.max)}
        </div>
      </div>
    </div>
  );
};
